import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { 
  ClipboardCheck, 
  Clock, 
  CheckCircle2,
  AlertTriangle,
  ChevronLeft,
  RefreshCw,
  Building2,
  Calendar,
  Wrench
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  "Compliant": "bg-green-100 text-green-700 border-green-200",
  "Due Soon": "bg-amber-100 text-amber-700 border-amber-200",
  "Overdue": "bg-red-100 text-red-700 border-red-200",
  "Failed": "bg-red-200 text-red-800 border-red-300",
  "No Due Date": "bg-slate-100 text-slate-600 border-slate-200",
};

const STATUS_ICONS = {
  "Compliant": <CheckCircle2 className="h-4 w-4 text-green-500" />,
  "Due Soon": <Clock className="h-4 w-4 text-amber-500" />,
  "Overdue": <AlertTriangle className="h-4 w-4 text-red-500" />,
  "Failed": <AlertTriangle className="h-4 w-4 text-red-600" />,
};

export default function ClientPortalV2PPMPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const navigate = useNavigate();

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      let url = `${API}/api/system-expansion/client-v2/ppm`;
      if (statusFilter && statusFilter !== "all") {
        url += `?status=${encodeURIComponent(statusFilter)}`;
      }
      
      const res = await axios.get(url, { headers });
      setData(res.data);
    } catch (err) {
      console.error("Failed to load PPM data:", err);
      toast.error("Failed to load PPM compliance");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const summary = data?.summary || {};
  const rows = data?.rows || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-v2-ppm-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-700 to-teal-600 p-6 text-white">
        <div className="max-w-6xl mx-auto">
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-white hover:text-white hover:bg-teal-600 mb-2"
            onClick={() => navigate('/client-v2')}
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to Dashboard
          </Button>
          <h1 className="text-2xl font-bold" data-testid="page-title">PPM Compliance</h1>
          <p className="text-teal-100 text-sm mt-1">Planned preventative maintenance status</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-l-4 border-l-teal-500" data-testid="total-card">
            <CardContent className="p-4">
              <div className="text-sm text-slate-500">Total Items</div>
              <div className="text-3xl font-bold text-teal-700">{summary.total || 0}</div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-green-500" data-testid="compliant-card">
            <CardContent className="p-4">
              <div className="text-sm text-slate-500">Compliant</div>
              <div className="text-3xl font-bold text-green-600">{summary.compliant || 0}</div>
              <div className="text-xs text-green-500">{summary.compliance_pct || 100}%</div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-amber-500" data-testid="due-soon-card">
            <CardContent className="p-4">
              <div className="text-sm text-slate-500">Due Soon</div>
              <div className="text-3xl font-bold text-amber-600">{summary.due_soon || 0}</div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-red-500" data-testid="overdue-card">
            <CardContent className="p-4">
              <div className="text-sm text-slate-500">Overdue</div>
              <div className="text-3xl font-bold text-red-600">{summary.overdue || 0}</div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="text-sm text-slate-500">Compliance</div>
              <div className="text-3xl font-bold text-purple-600">{summary.compliance_pct || 100}%</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <span className="text-sm text-slate-600">Filter by status:</span>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Compliant">Compliant</SelectItem>
                <SelectItem value="Due Soon">Due Soon</SelectItem>
                <SelectItem value="Overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="ghost" size="sm" onClick={loadData} data-testid="refresh-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </CardContent>
        </Card>

        {/* PPM Items */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4 text-teal-600" />
              Compliance Items ({rows.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <ClipboardCheck className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No PPM items found</p>
              </div>
            ) : (
              <div className="overflow-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-slate-500 text-left">
                      <th className="pb-3 font-medium">Park</th>
                      <th className="pb-3 font-medium">Unit</th>
                      <th className="pb-3 font-medium">Type</th>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium">Last Completed</th>
                      <th className="pb-3 font-medium">Next Due</th>
                      <th className="pb-3 font-medium">Active Job</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr 
                        key={row.schedule_id} 
                        className="border-b last:border-b-0 hover:bg-slate-50"
                        data-testid={`ppm-row-${row.schedule_id}`}
                      >
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-slate-400" />
                            {row.park_name || "-"}
                          </div>
                        </td>
                        <td className="py-3">{row.unit_name || "-"}</td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <Wrench className="h-4 w-4 text-slate-400" />
                            <div>
                              <div className="font-medium">{row.template_name}</div>
                              <div className="text-xs text-slate-400">{row.trade_type}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3">
                          <Badge className={STATUS_STYLES[row.status] || "bg-slate-100"}>
                            <span className="flex items-center gap-1">
                              {STATUS_ICONS[row.status]}
                              {row.status}
                            </span>
                          </Badge>
                        </td>
                        <td className="py-3">
                          {row.last_completed_date 
                            ? new Date(row.last_completed_date).toLocaleDateString() 
                            : <span className="text-slate-400">Never</span>
                          }
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-slate-400" />
                            {row.next_due_date 
                              ? new Date(row.next_due_date).toLocaleDateString() 
                              : "-"
                            }
                          </div>
                        </td>
                        <td className="py-3">
                          {row.active_job_reference ? (
                            <span className="text-teal-600 font-medium">{row.active_job_reference}</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
